
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {Database, MapPin, Map, Workflow, Building2, Droplets, BarChart3, TrendingUp, Upload, Home, BookOpen, Users, Award, Target, Download, Info, X} from 'lucide-react';

const HomePage = () => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('home');
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [infoTab, setInfoTab] = useState<'acerca' | 'variables' | 'jerarquizacion' | 'geovisualizador'>('acerca');

  React.useEffect(() => {
    const hasVisited = localStorage.getItem('hasVisitedBefore');
    if (!hasVisited) {
      setShowWelcomeModal(true);
      localStorage.setItem('hasVisitedBefore', 'true');
    }
  }, []);

  const handlePlatformAccess = () => {
    window.open('https://ee-serchjimenez1990.projects.earthengine.app/view/jerarquizacion', '_blank');
  };

  const handleDatabaseAccess = () => {
    window.open('https://www.appsheet.com/start/5a053420-e831-4adc-b54f-da5bd88f0f9d', '_blank');
  };

  const handleDatabaseTitleClick = () => {
    window.open('https://www.appsheet.com/start/117f5453-bc58-4bb9-b738-c6440bce9d90', '_blank');
  };

  const handleInteractiveAnalysis = () => {
    navigate('/analisis-interactivo');
  };

  const handleDatabaseUpload = () => {
    navigate('/base-datos-upload');
  };

  const handleMethodologyNavigation = () => {
    navigate('/metodologia');
  };

  const handleInstitutionsNavigation = () => {
    navigate('/instituciones');
  };

  const handleMapotecaNavigation = () => {
    navigate('/mapoteca');
  };

  const handleVisualizador = () => {
    navigate('/visualizador');
  };

  const renderHomeSection = () => {
    const cardMotion = {
      whileHover: { scale: 1.05, y: -5 },
      whileTap: { scale: 0.95 }
    };

    return (
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 1, delay: 0.3 }}
        className="text-center mb-12">

      {/* Título principal */}
      <motion.h1
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight mt-24">
        Microcuencas de
        <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-green-400 py-2">
          Chiapas
        </span>
      </motion.h1>

      <motion.p
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.7 }}
          className="text-xl md:text-2xl text-blue-100 mb-4 max-w-4xl mx-auto">Sistema de visualización de Jerarquización de microcuencas Hidrográficas

    </motion.p>

      <motion.p
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.9 }}
          className="text-lg text-blue-200 mb-12 max-w-3xl mx-auto">Identifica las microcuencas más importantes del estado mediante análisis de diferentes variables

    </motion.p>

      {/* Botones principales */}
      <motion.div
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8, delay: 1.1 }}
          className="flex flex-wrap justify-center gap-6 mb-16 max-w-7xl mx-auto">

        {/* Botón Análisis Interactivo */}
        



        <motion.button
            whileHover={{ scale: 1.05, y: -5 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate('/visualizacion-datos')}
            className="group relative bg-gradient-to-r from-pink-600 to-pink-700 hover:from-pink-700 hover:to-pink-800 text-white px-8 py-6 rounded-2xl font-semibold text-lg shadow-2xl transition-all duration-300">
          <div className="absolute inset-0 bg-gradient-to-r from-pink-500 to-pink-600 rounded-2xl blur opacity-50 group-hover:opacity-75 transition-opacity duration-300" />
          <div className="relative flex items-center justify-center space-x-3">
            <BarChart3 className="w-6 h-6" />
            <span>Variables</span>
          </div>
          <div className="absolute inset-0 rounded-2xl bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        </motion.button>


        {/* Botón Jerarquización Completa */}
        <motion.button
            whileHover={{ scale: 1.05, y: -5 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate('/jerarquizacion-completa')}
            className="group relative bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white px-8 py-6 rounded-2xl font-semibold text-lg shadow-2xl transition-all duration-300">
          <div className="absolute inset-0 bg-gradient-to-r from-purple-500 to-purple-600 rounded-2xl blur opacity-50 group-hover:opacity-75 transition-opacity duration-300" />
          <div className="relative flex items-center justify-center space-x-3">
            <TrendingUp className="w-6 h-6" />
            <span>Jerarquización</span>
          </div>
          <div className="absolute inset-0 rounded-2xl bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
        </motion.button>

        {/* Botón Datos Completos */}



        {/* Botón Visualizador */}
        <motion.button
            whileHover={{ scale: 1.05, y: -5 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleVisualizador}
            className="group relative bg-gradient-to-r from-cyan-600 to-cyan-700 hover:from-cyan-700 hover:to-cyan-800 text-white px-8 py-6 rounded-2xl font-semibold text-lg shadow-2xl transition-all duration-300">
          <div className="absolute inset-0 bg-gradient-to-r from-cyan-500 to-cyan-600 rounded-2xl blur opacity-50 group-hover:opacity-75 transition-opacity duration-300" />
          <div className="relative flex items-center justify-center space-x-3">
            <Map className="w-6 h-6" />
            <span>Geo Visualizador</span>
          </div>
          <div className="absolute inset-0 rounded-2xl bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        </motion.button>
          {/*
                  <div className="relative flex items-center gap-2">
                   <motion.button
                       whileHover={{ scale: 1.05, y: -5 }}
                       whileTap={{ scale: 0.95 }}
                       onClick={handlePlatformAccess}
                       className="group relative bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-8 py-6 rounded-2xl font-semibold text-lg shadow-2xl transition-all duration-300">
                     <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-blue-600 rounded-2xl blur opacity-50 group-hover:opacity-75 transition-opacity duration-300" />
                     <div className="relative flex items-center justify-center space-x-3">
                       <MapPin className="w-6 h-6" />
                       <span>Analisis Espacial</span>
                     </div>
                     <div className="absolute inset-0 rounded-2xl bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                   </motion.button>
                   
                   <motion.button
                       whileHover={{ scale: 1.1 }}
                       whileTap={{ scale: 0.9 }}
                       onClick={() => setShowRegistrationModal(true)}
                       className="bg-blue-500/80 hover:bg-blue-600 text-white p-2 rounded-full shadow-lg transition-all duration-300">
                     <Info className="w-5 h-5" />
                   </motion.button>
                  </div>
                  */}

      </motion.div>

      {/* Características destacadas */}
      <motion.div
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8, delay: 1.3 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto place-items-center">



        <motion.button
            {...cardMotion}
            onClick={handleMethodologyNavigation}
            className="w-full text-center bg-white/10 backdrop-blur-md rounded-xl p-8 border border-white/20
                    hover:bg-white/20 transition-all duration-300 cursor-pointer min-h-[180px] flex flex-col items-center justify-center">











          <Workflow className="w-12 h-12 text-green-400 mx-auto mb-4" />
          <h3 className="text-white font-semibold mb-3 text-xl">
            Metodología
          </h3>
          <p className="text-blue-200 text-base">Metodología General</p>
        </motion.button>

        <motion.button
            {...cardMotion}
            onClick={handleMapotecaNavigation}
            className="w-full text-center bg-white/10 backdrop-blur-md rounded-xl p-8 border border-white/20
                    hover:bg-white/20 transition-all duration-300 cursor-pointer min-h-[180px] flex flex-col items-center justify-center">











          <Map className="w-12 h-12 text-purple-400 mx-auto mb-4" />
          <h3 className="text-white font-semibold mb-3 text-xl">
            Mapoteca
          </h3>
          <p className="text-blue-200 text-base">Mapas y archivos descargables</p>
        </motion.button>

        <motion.button
            whileHover={{ scale: 1.05, y: -5 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate('/archivos-descargables')}
            className="w-full text-center bg-white/10 backdrop-blur-md rounded-xl p-8 border border-white/20 hover:bg-white/20 transition-all duration-300 cursor-pointer min-h-[180px] flex flex-col items-center justify-center">
          <Download className="w-12 h-12 text-cyan-400 mx-auto mb-4" />
          <h3 className="text-white font-semibold mb-3 text-xl">
            Archivos Descargables
          </h3>
          <p className="text-blue-200 text-base">Rasters y datos geoespaciales</p>
        </motion.button>



        <div className="text-center bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20 hidden">
          <Database className="w-8 h-8 text-orange-400 mx-auto mb-3" />
          <h3
              onClick={handleDatabaseTitleClick}
              className="text-white font-semibold mb-2 cursor-pointer hover:text-orange-300 transition-colors duration-200">
            Base de Datos
          </h3>
          <p className="text-blue-200 text-sm">Información completa de recursos</p>
        </div>
      </motion.div>


    </motion.div>);

  };

  const renderInstitutionsSection = () =>
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.8 }}
    className="text-center mb-12">

      <motion.h2
      className="text-4xl md:text-6xl font-bold text-white mb-8">
        Instituciones
        <span className="block text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-blue-400">
          Participantes
        </span>
      </motion.h2>

      <motion.p
      className="text-xl text-blue-100 mb-12 max-w-4xl mx-auto">
        Colaboración interinstitucional para el desarrollo del Sistema de Jerarquización de Microcuencas
      </motion.p>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
        {/* Gobierno del Estado de Chiapas */}
        <motion.div
        whileHover={{ scale: 1.05 }}
        className="bg-white/10 backdrop-blur-md rounded-xl p-8 border border-white/20 text-center">
          <Building2 className="w-12 h-12 text-blue-400 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-white mb-3">Gobierno del Estado de Chiapas</h3>
          <p className="text-blue-200 text-sm mb-4">Secretaría de Medio Ambiente e Historia Natural</p>
          <div className="flex justify-center">
            <Award className="w-6 h-6 text-yellow-400" />
          </div>
        </motion.div>

        {/* Universidad de Ciencias y Artes de Chiapas */}
        <motion.div
        whileHover={{ scale: 1.05 }}
        className="bg-white/10 backdrop-blur-md rounded-xl p-8 border border-white/20 text-center">
          <Users className="w-12 h-12 text-green-400 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-white mb-3">UNICACH</h3>
          <p className="text-blue-200 text-sm mb-4">Universidad de Ciencias y Artes de Chiapas</p>
          <div className="flex justify-center">
            <Award className="w-6 h-6 text-yellow-400" />
          </div>
        </motion.div>

        {/* Instituto de Investigación */}
        <motion.div
        whileHover={{ scale: 1.05 }}
        className="bg-white/10 backdrop-blur-md rounded-xl p-8 border border-white/20 text-center">
          <Target className="w-12 h-12 text-purple-400 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-white mb-3">Centro de Investigación</h3>
          <p className="text-blue-200 text-sm mb-4">Estudios Ambientales y Recursos Hídricos</p>
          <div className="flex justify-center">
            <Award className="w-6 h-6 text-yellow-400" />
          </div>
        </motion.div>
      </div>

      <motion.div
      className="mt-12 bg-white/5 backdrop-blur-md rounded-xl p-8 border border-white/10 max-w-4xl mx-auto">
        <h3 className="text-2xl font-bold text-white mb-4">Colaboración Técnica</h3>
        <p className="text-blue-200 mb-4">
          Este proyecto es resultado de la colaboración entre instituciones gubernamentales, 
          académicas y de investigación, uniendo esfuerzos para el desarrollo de herramientas 
          científicas para la gestión sustentable de los recursos hídricos en Chiapas.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div className="text-center">
            <div className="text-blue-400 font-semibold">Coordinación</div>
            <div className="text-blue-200">Gobierno Estatal</div>
          </div>
          <div className="text-center">
            <div className="text-green-400 font-semibold">Investigación</div>
            <div className="text-blue-200">Instituciones Académicas</div>
          </div>
          <div className="text-center">
            <div className="text-purple-400 font-semibold">Desarrollo</div>
            <div className="text-blue-200">Centros Especializados</div>
          </div>
        </div>

        {/* Botón para ir a la página completa de instituciones */}
        <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={handleInstitutionsNavigation}
        className="mt-6 bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 text-white px-8 py-4 rounded-xl font-semibold text-lg shadow-lg transition-all duration-300">
          <div className="flex items-center space-x-3">
            <Building2 className="w-6 h-6" />
            <span>Ver Información Completa</span>
          </div>
        </motion.button>
      </motion.div>
    </motion.div>;


  const renderMethodologySection = () =>
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.8 }}
    className="text-center mb-12">

      <motion.h2
      className="text-4xl md:text-6xl font-bold text-white mb-8">
        Metodología
        <span className="block text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-green-400">
          General
        </span>
      </motion.h2>

      <motion.p
      className="text-xl text-blue-100 mb-12 max-w-4xl mx-auto">
        Enfoque científico multicriterio para la jerarquización de microcuencas hidrográficas
      </motion.p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto mb-8">
        {/* Criterios de Evaluación */}
        <motion.div
        whileHover={{ scale: 1.02 }}
        className="bg-white/10 backdrop-blur-md rounded-xl p-8 border border-white/20">
          <Droplets className="w-12 h-12 text-blue-400 mx-auto mb-4" />
          <h3 className="text-2xl font-bold text-white mb-4">Criterios de Evaluación</h3>
          <div className="space-y-3 text-left">
            <div className="flex items-center space-x-3">
              <div className="w-3 h-3 bg-red-400 rounded-full"></div>
              <span className="text-blue-200">Vulnerabilidad Física (30%)</span>
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-3 h-3 bg-blue-400 rounded-full"></div>
              <span className="text-blue-200">Vulnerabilidad Climática (25%)</span>
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-3 h-3 bg-green-400 rounded-full"></div>
              <span className="text-blue-200">Vulnerabilidad Ambiental (25%)</span>
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-3 h-3 bg-purple-400 rounded-full"></div>
              <span className="text-blue-200">Vulnerabilidad Social (20%)</span>
            </div>
          </div>
        </motion.div>

        {/* Proceso de Análisis */}
        <motion.div
        whileHover={{ scale: 1.02 }}
        className="bg-white/10 backdrop-blur-md rounded-xl p-8 border border-white/20">
          <BarChart3 className="w-12 h-12 text-green-400 mx-auto mb-4" />
          <h3 className="text-2xl font-bold text-white mb-4">Proceso de Análisis</h3>
          <div className="space-y-3 text-left">
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold mt-0.5">1</div>
              <span className="text-blue-200 text-sm">Recolección de datos geoespaciales</span>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold mt-0.5">2</div>
              <span className="text-blue-200 text-sm">Normalización de variables</span>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold mt-0.5">3</div>
              <span className="text-blue-200 text-sm">Aplicación de pesos multicriterio</span>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold mt-0.5">4</div>
              <span className="text-blue-200 text-sm">Cálculo del índice de priorización</span>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Fórmula y Botón de Metodología Completa */}
      <motion.div
      className="bg-white/5 backdrop-blur-md rounded-xl p-8 border border-white/10 max-w-4xl mx-auto">
        <h3 className="text-2xl font-bold text-white mb-4">Modelo Matemático</h3>
        <div className="bg-gray-900/50 rounded-lg p-6 mb-6">
          <div className="font-mono text-xl text-center text-white mb-2">
            IP = 0.30 × VF + 0.25 × VC + 0.25 × VA + 0.20 × VS
          </div>
          <p className="text-blue-200 text-sm text-center">
            Donde IP = Índice de Priorización, VF = Vulnerabilidad Física, 
            VC = Vulnerabilidad Climática, VA = Vulnerabilidad Ambiental, VS = Vulnerabilidad Social
          </p>
        </div>
        
        <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={handleMethodologyNavigation}
        className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white px-8 py-4 rounded-xl font-semibold text-lg shadow-lg transition-all duration-300">
          <div className="flex items-center space-x-3">
            <BookOpen className="w-6 h-6" />
            <span>Ver Metodología Completa</span>
          </div>
        </motion.button>
      </motion.div>
    </motion.div>;


  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Imagen de fondo con overlay */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: 'url(https://images.pexels.com/photos/35221734/pexels-photo-35221734.jpeg)'
        }}>
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/80 via-green-900/70 to-blue-800/90" />
        <div className="absolute inset-0 bg-black/20" />
      </div>

      {/* Barra superior con instituciones */}
      <motion.div
        initial={{ y: -50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6 }}
        className="relative z-20 backdrop-blur-md border-b border-white/10 text-[#D4D4D4] bg-[#F9FAFB]">
        <div className="container mx-auto px-6 py-3">
          <div className="flex items-center justify-between">
            <span className="text-[#000000] text-base font-medium">Instituciones Colaboradoras:</span>
            <div className="flex items-center gap-6">
              <a
                href="https://www.inifap.gob.mx"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:scale-110 transition-transform duration-300">
                <img
                  src="https://drive.google.com/thumbnail?id=1eND3SZKGgme-hOLe8VwIA1ednDuy-N1I"
                  alt="INIFAP"
                  className="h-10 w-auto object-contain" />
              </a>
              <a
                href="https://www.nature.org/es-us"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:scale-110 transition-transform duration-300">
                <img
                  src="https://drive.google.com/thumbnail?id=18apDrZER22jMchBronddteUBjDDQ6WxS"
                  alt="The Nature Conservancy"
                  className="h-10 w-auto object-contain" />
              </a>
              <a
                href="https://www.chiapas.gob.mx"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:scale-110 transition-transform duration-300">
                <img
                  src="https://drive.google.com/thumbnail?id=1duwqer-xU6eTS_HPqOYCj9mHgifSoUSR"
                  alt="Gobierno de Chiapas"
                  className="h-10 w-auto object-contain" />
              </a>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Contenido principal */}
      <div className="relative z-10 flex flex-col items-center justify-center min-h-screen px-6 -mt-20 pb-24">
        {activeSection === 'home' && renderHomeSection()}
        {activeSection === 'institutions' && renderInstitutionsSection()}
        {activeSection === 'methodology' && renderMethodologySection()}
      </div>

      {/* Footer */}
      <motion.footer
        initial={{ y: 50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, delay: 1.5 }}
        className="fixed bottom-0 left-0 right-0 z-10 bg-black/30 backdrop-blur-md border-t border-white/20">
        <div className="container mx-auto px-6 py-6">
          <div className="text-center text-blue-200 flex items-center justify-center gap-3">
          <button
              onClick={() => {
                setInfoTab('acerca');
                setShowInfoModal(true);
              }}
              className="flex items-center gap-2 text-blue-200 hover:text-white transition-colors duration-200 underline pt-[4px] pl-[4px] pr-[4px] pb-[4px]">

            <Info className="w-4 h-4" />
            Información para el usuario
          </button>
            <button
              onClick={() => setShowWelcomeModal(true)}
              className="flex items-center gap-1 text-blue-200/60 hover:text-white hover:scale-110 transition-all duration-200 p-1 rounded-full"
              title="Ver bienvenida">
              <Home className="w-4 h-4" />
              Bienvenida
            </button>
          </div>
        </div>
      </motion.footer>



      {/* Modal de Bienvenida */}
      {showWelcomeModal &&
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={() => setShowWelcomeModal(false)}>
          <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.4, type: "spring" }}
          className="bg-white rounded-2xl max-w-6xl w-full shadow-2xl overflow-hidden"
          onClick={(e) => e.stopPropagation()}>
            
            {/* Layout Horizontal */}
            <div className="flex flex-col lg:flex-row max-h-[85vh]">
              
              {/* Lado Izquierdo - Contenido Principal */}
              <div className="lg:w-1/2 p-8 lg:p-10 overflow-y-auto">
                <div className="mb-6">
                  






                  <h2 className="text-4xl font-bold text-gray-900 mb-2">¡Bienvenido!</h2>
                  <p className="text-lg text-gray-600">Sistema de Jerarquización de Microcuencas de Chiapas</p>
                </div>
                
                {/* Descripción */}
                <div className="mb-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Info className="w-5 h-5 text-blue-600" />
                    Acerca de la Plataforma
                  </h3>
                  <p className="text-gray-700 leading-relaxed">Esta plataforma es una herramienta desarrollada por INIFAP en colaboración con instituciones asociadas para el análisis de 79 microcuencas hidrográficas en Chiapas. Aquí podrás visualizar datos ambientales, sociales y geográficos, explorar mapas interactivos, y acceder a información jerarquizada.</p>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Info className="w-5 h-5 text-blue-600" />
                    Contacto
                  </h3>
                  <p className="text-gray-700 leading-relaxed">Dra. Alma Liz Vargas de la Mora (vargas.alma@inifap.gob.mx).</p>
                </div>

                {/* Manual de Usuario */}
                <div className="bg-gradient-to-br from-blue-50 to-green-50 rounded-xl p-5 border border-blue-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
                    <BookOpen className="w-5 h-5 text-blue-600" />
                    Manual de Usuario
                  </h3>
                  <p className="text-gray-700 text-sm mb-3">Descarga el manual para conocer todas las funcionalidades.</p>
                  <a
                  href="https://drive.google.com/uc?export=download&id=1_RTTkKR3mtRx8CEpvK0eNRj675UidavX"
                  download
                  className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-green-600 hover:from-blue-700 hover:to-green-700 text-white px-5 py-2.5 rounded-lg font-semibold transition-all duration-300 shadow-md text-sm">
                    <Download className="w-4 h-4" />
                    Descargar Manual PDF
                  </a>
                </div>

                <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setShowWelcomeModal(false)}
                className="mt-6 w-full bg-gradient-to-r from-blue-600 to-green-600 hover:from-blue-700 hover:to-green-700 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300 shadow-lg">
                  Comenzar a Explorar
                </motion.button>
              </div>

              {/* Lado Derecho - Video */}
              <div className="lg:w-1/2 bg-gradient-to-br from-blue-500 to-green-500 p-8 lg:p-10 flex flex-col justify-center">
                <h3 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                  <BookOpen className="w-6 h-6" />
                  Video Introductorio
                </h3>
                <div className="aspect-video bg-white/10 backdrop-blur-sm rounded-xl overflow-hidden shadow-2xl border-2 border-white/30">
                  <iframe
                  className="w-full h-full"
                  src="https://www.youtube.com/embed/Mt1wWiQgHVA"
                  title="Video Tutorial"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen>
                  </iframe>
                </div>
                <p className="text-white/90 text-sm mt-4 text-center">
                  Aprende a utilizar todas las herramientas de la plataforma
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      }

      {/* Modal de Información para el Usuario */}
      {showInfoModal &&
      <div
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={() => setShowInfoModal(false)}>
          <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="bg-white/95 backdrop-blur-md rounded-2xl p-8 max-w-3xl w-full shadow-2xl max-h-[85vh] overflow-y-auto relative"
          onClick={(e) => e.stopPropagation()}>
            <button
            onClick={() => setShowInfoModal(false)}
            className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-full transition-colors duration-200">
              <X className="w-6 h-6 text-gray-600" />
            </button>
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-amber-100 p-3 rounded-full">
                <Info className="w-8 h-8 text-amber-600" />
              </div>
              <h2 className="text-3xl font-bold text-gray-800">Información para el Usuario</h2>
            </div>
            
            {/* Pestañas */}
            <div className="flex gap-2 mb-6 border-b border-gray-200">
              <button
              onClick={() => setInfoTab('variables')}
              className={`px-6 py-3 font-semibold transition-all duration-300 border-b-2 ${
              infoTab === 'variables' ?
              'border-pink-600 text-pink-600' :
              'border-transparent text-gray-500 hover:text-gray-700'}`
              }>
                Variables
              </button>
              <button
              onClick={() => setInfoTab('jerarquizacion')}
              className={`px-6 py-3 font-semibold transition-all duration-300 border-b-2 ${
              infoTab === 'jerarquizacion' ?
              'border-purple-600 text-purple-600' :
              'border-transparent text-gray-500 hover:text-gray-700'}`
              }>
                Jerarquización
              </button>
              <button
              onClick={() => setInfoTab('geovisualizador')}
              className={`px-6 py-3 font-semibold transition-all duration-300 border-b-2 ${
              infoTab === 'geovisualizador' ?
              'border-cyan-600 text-cyan-600' :
              'border-transparent text-gray-500 hover:text-gray-700'}`
              }>
                Geo Visualizador
              </button>
              <button
              onClick={() => setInfoTab('acerca')}
              className={`px-6 py-3 font-semibold transition-all duration-300 border-b-2 ${
              infoTab === 'acerca' ?
              'border-blue-600 text-blue-600' :
              'border-transparent text-gray-500 hover:text-gray-700'}`
              }>
                Acerca de
              </button>
            </div>

            {/* Contenido según pestaña */}
            <div className="space-y-6 text-gray-700">
              {infoTab === 'variables' &&
            <>
                  <div className="bg-pink-50 border-l-4 border-pink-500 p-5 rounded">
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">Página de Variables</h3>
                    <p className="leading-relaxed mb-3">
                      La sección de <strong>Variables</strong> permite visualizar y analizar las diferentes variables 
                      (ambientales, sociales y geográfics) de importancia para el analisis de las 79 microcuencas de Chiapas.
                    </p>
                  </div>

                  <div className="bg-white border border-pink-200 p-5 rounded-lg">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">¿Qué información contiene?</h3>
                    <div className="space-y-3">
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-pink-500 rounded-full mt-2"></div>
                        <div>
                          <strong>Variables Ambientales:</strong> Erosión del suelo, tasa de deforestación, 
                          fracción de escurrimiento, superficie afectada por incendios, importancia para la conservación
                        </div>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-pink-500 rounded-full mt-2"></div>
                        <div>
                          <strong>Variables Sociales:</strong> Densidad de población, índice de rezago social, 
                          superficie de uso agropecuario
                        </div>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-pink-500 rounded-full mt-2"></div>
                        <div>
                          <strong>Variables Geográficas:</strong> Código de microcuenca, nombre, municipio, 
                          superficie, perímetro
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-50 p-5 rounded-lg">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Funcionalidades</h3>
                    <ul className="list-disc list-inside space-y-2 text-sm">
                      <li>Tabla interactiva con todas las variables por microcuenca</li>
                      <li>Gráficas dinámicas para comparar variables</li>
                      <li>Filtros por microcuenca y municipio</li>
                      <li>Exportación de datos y gráficas a PDF</li>
                      <li>Información detallada de cada variable</li>
                    </ul>
                  </div>
                </>
            }

              {infoTab === 'jerarquizacion' &&
            <>
                  <div className="bg-purple-50 border-l-4 border-purple-500 p-5 rounded">
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">Página de Jerarquización</h3>
                    <p className="leading-relaxed mb-3">
                      La sección de <strong>Jerarquización</strong> presenta el orden de prioridad de las 
                      microcuencas basado en un análisis multicriterio que integra vulnerabilidades físicas, 
                      climáticas, ambientales y sociales.
                    </p>
                  </div>

                  <div className="bg-white border border-purple-200 p-5 rounded-lg">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">¿Qué información contiene?</h3>
                    <div className="space-y-3">
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-purple-500 rounded-full mt-2"></div>
                        <div>
                          <strong>Índice de Priorización Total:</strong> Clasificación general de las 79 microcuencas 
                          (1 = mayor prioridad, 79 = menor prioridad)
                        </div>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-purple-500 rounded-full mt-2"></div>
                        <div>
                          <strong>Indicadores por Variable:</strong> Jerarquización individual para erosión, 
                          escurrimiento, deforestación, incendios, uso agropecuario, conservación, densidad poblacional 
                          y rezago social
                        </div>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-purple-500 rounded-full mt-2"></div>
                        <div>
                          <strong>Clasificación por Colores:</strong> Visualización mediante códigos de colores 
                          que indican el nivel de prioridad (rojo = muy alta, verde = muy baja)
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-50 p-5 rounded-lg">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Funcionalidades</h3>
                    <ul className="list-disc list-inside space-y-2 text-sm">
                      <li>Tabla completa con jerarquización de las 79 microcuencas</li>
                      <li>Indicadores con códigos de colores según nivel de prioridad</li>
                      <li>Información detallada de pesos y definiciones de cada indicador</li>
                      <li>Ordenamiento por columnas para facilitar el análisis</li>
                      <li>Visualización de detalles por microcuenca al hacer clic</li>
                    </ul>
                  </div>
                </>
            }

              {infoTab === 'geovisualizador' &&
            <>
                  <div className="bg-cyan-50 border-l-4 border-cyan-500 p-5 rounded">
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">Geo Visualizador</h3>
                    <p className="leading-relaxed mb-3">
                      El <strong>Geo Visualizador</strong> es una herramienta de mapeo interactivo que permite 
                      explorar geográficamente las 79 microcuencas de Chiapas y su ubicación espacial.
                    </p>
                  </div>

                  <div className="bg-white border border-cyan-200 p-5 rounded-lg">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">¿Qué información contiene?</h3>
                    <div className="space-y-3">
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-cyan-500 rounded-full mt-2"></div>
                        <div>
                          <strong>Mapa Interactivo:</strong> Visualización de las 79 microcuencas sobre un mapa 
                          base de OpenStreetMap con delimitación de límites hidrográficos y su nivel de jerarquización
                        </div>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-cyan-500 rounded-full mt-2"></div>
                        <div>
                          <strong>Información Geográfica:</strong> Ubicación precisa de cada microcuenca, 
                          municipios involucrados, y relación espacial entre ellas
                        </div>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-cyan-500 rounded-full mt-2"></div>
                        <div>
                          <strong>Capas Temáticas:</strong> Superposición de información adicional sobre el territorio 
                          para análisis espacial integrado
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-50 p-5 rounded-lg">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Funcionalidades</h3>
                    <ul className="list-disc list-inside space-y-2 text-sm">
                      <li>Mapa embebido con navegación interactiva (zoom, pan)</li>
                      <li>Identificación visual de límites de microcuencas</li>
                      <li>Visualización de contexto geográfico y municipios</li>
                      <li>Acceso a vista de pantalla completa para análisis detallado</li>
                      <li>Integración con OpenStreetMap para cartografía actualizada</li>
                    </ul>
                  </div>
                </>
            }
              {infoTab === 'acerca' &&
            <>
                  <div className="bg-blue-50 border-l-4 border-blue-500 p-5 rounded">
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Licencia de Datos</h3>
                    <p className="leading-relaxed">
                      Los datos y mapas presentados en esta plataforma son producto del trabajo colaborativo 
                      entre las instituciones participantes utilizando información publica disponible en diferentes fuentes.
                    </p>
                  </div>

                  <div className="bg-white border border-blue-200 p-5 rounded-lg">
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Uso de la Información</h3>
                    <p className="leading-relaxed">La información contenida en este sistema está disponible para uso público con fines 
                      educativos, de investigación y planificación. Se solicita citar la fuente apropiadamente. Para mayor información comunicarse al correo vargas.alma@inifap.gob.mx

                </p>
                  </div>

                  <div className="bg-gray-50 p-5 rounded-lg">
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Créditos</h3>
                    <p className="leading-relaxed">
                      © 2025 INIFAP - Instituto Nacional de Investigaciones Forestales, Agrícolas y Pecuarias.
                      <br />
                      Desarrollado en colaboración con instituciones participantes.
                    </p>
                  </div>
                </>
            }
              <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                <h3 className="text-base font-semibold text-gray-900 mb-2">Cobertura Total</h3>
                <p className="text-sm text-gray-700">
                  Las tres secciones (Variables, Jerarquización y Geo-visualizador) cubren un total de <strong>79 microcuencas hidrográficas</strong> distribuidas 
                  en diferentes municipios del estado de Chiapas, analizadas con metodología científica validada.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      }

      {/* Modal de Registro */}
      {showRegistrationModal &&
      <div
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={() => setShowRegistrationModal(false)}>
          <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="bg-white/95 backdrop-blur-md rounded-2xl p-8 max-w-2xl w-full shadow-2xl"
          onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-blue-100 p-3 rounded-full">
                <Info className="w-8 h-8 text-blue-600" />
              </div>
              <h2 className="text-3xl font-bold text-gray-800">Acceso a Análisis Espacial</h2>
            </div>
            
            <div className="space-y-4 text-gray-700">
              <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Registro Requerido</h3>
                <p className="leading-relaxed">
                  Para acceder a la plataforma de Análisis Espacial es necesario registrarse previamente.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">¿Cómo solicitar acceso?</h3>
                <p className="leading-relaxed mb-3">
                  Envíe un correo electrónico con la siguiente información:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Su nombre completo</li>
                  <li>Institución u organización (si aplica)</li>
                  <li>Motivo de la solicitud de acceso</li>
                </ul>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Contacto:</h3>
                <a
                href="mailto:jimenez.sergio@inifap.gob.mx?subject=Solicitud de Acceso - Análisis Espacial de Microcuencas"
                className="text-blue-600 hover:text-blue-800 font-semibold text-lg underline">
                  jimenez.sergio@inifap.gob.mx
                </a>
                <p className="text-sm text-gray-600 mt-2">
                  Haga clic en el correo para abrir su cliente de email automáticamente
                </p>
              </div>
            </div>

            <button
            onClick={() => setShowRegistrationModal(false)}
            className="mt-6 w-full bg-gradient-to-r from-blue-600 to-green-600 hover:from-blue-700 hover:to-green-700 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300">
              Entendido
            </button>
          </motion.div>
        </div>
      }
    </div>
  );

};

export default HomePage;